/**
 * Утилиты для работы с валютами, ценами и скидками
 * Используются для форматирования и отображения финансовой информации в приемках
 */

export const CURRENCY_SYMBOLS = {
    'RUB': '₽',
    'USD': '$',
    'EUR': '€',
    'CNY': '¥',
    'GBP': '£',
    'CHF': 'Fr',
    'JPY': '¥',
    'KZT': '₸',
    'BYN': 'Br',
    'UAH': '₴',
};

/**
 * Получение символа валюты
 * @param {string} currencyCode - Код валюты (RUB, USD, CNY, ...)
 * @returns {string} - Символ валюты или пустая строка
 */
export const getCurrencySymbol = (currencyCode) => {
    if (!currencyCode) return '';
    return CURRENCY_SYMBOLS[currencyCode] || currencyCode;
};

/**
 * Форматирование цены с валютой
 * @param {number} price - Цена товара
 * @param {string} currencyCode - Код валюты
 * @returns {string} - Отформатированная цена с символом валюты
 */
export const formatPrice = (price, currencyCode) => {
    if (price === undefined || price === null || price === '') return '-';

    const numPrice = Number(price);
    if (isNaN(numPrice)) return '-';

    const symbol = getCurrencySymbol(currencyCode);
    const formattedPrice = numPrice.toFixed(2);

    if (symbol) {
        return `${formattedPrice} ${symbol}`;
    }

    return currencyCode ? `${formattedPrice} ${currencyCode}` : formattedPrice;
};

/**
 * Форматирование скидки/наценки
 * @param {number} discount - Скидка в процентах (может быть отрицательной для наценки)
 * @returns {string} - Отформатированная скидка с указанием типа
 */
export const formatDiscount = (discount) => {
    if (discount === undefined || discount === null || discount === '' || discount === 0) return '-';

    const numDiscount = Number(discount);
    if (isNaN(numDiscount)) return '-';

    // Отрицательное (-10%) → "-10%" красным (наценка)
    // Положительное (+10%) → "+10%" зеленым (скидка)
    const sign = numDiscount > 0 ? '+' : '-';
    const value = Math.abs(numDiscount).toFixed(1);

    return `${sign}${value}%`;
};

/**
 * Определение типа скидки
 * @param {number} discount - Скидка в процентах
 * @returns {string} - 'discount' | 'markup' | 'none'
 */
export const getDiscountType = (discount) => {
    if (!discount || discount === 0) return 'none';
    return discount > 0 ? 'discount' : 'markup';
};

/**
 * Название для скидки/наценки
 * @param {number} discount - Скидка в процентах
 * @returns {string} - Текстовая метка
 */
export const getDiscountLabel = (discount) => {
    const type = getDiscountType(discount);
    switch (type) {
        case 'discount': return 'Скидка';
        case 'markup': return 'Наценка';
        default: return '-';
    }
};

/**
 * Получение цвета для отображения скидки/наценки
 * @param {number} discount - Скидка в процентах
 * @returns {object} - Объект с цветами { color, backgroundColor, borderColor }
 */
export const getDiscountColors = (discount) => {
    const type = getDiscountType(discount);

    switch (type) {
        case 'discount':
            return {
                color: '#2e7d32',        // Темно-зеленый текст
                backgroundColor: '#e8f5e8',  // Светло-зеленый фон
                borderColor: '#c8e6c9',     // Светло-зеленая рамка
                chipColor: 'success'
            };
        case 'markup':
            return {
                color: '#c62828',        // Темно-красный текст
                backgroundColor: '#ffebee',  // Светло-красный фон
                borderColor: '#ffcdd2',     // Светло-красная рамка
                chipColor: 'error'
            };
        default:
            return {
                color: '#666',
                backgroundColor: 'transparent',
                borderColor: '#ddd',
                chipColor: 'default'
            };
    }
};

/**
 * Форматирование курса валюты
 * @param {number} rateValue - Значение курса
 * @param {string} currencyCode - Код валюты
 * @returns {string} - Отформатированный курс
 */
export const formatRate = (rateValue, currencyCode) => {
    if (!rateValue || !currencyCode) return '-';

    const symbol = getCurrencySymbol(currencyCode);
    return `${rateValue.toFixed(2)} ${symbol || currencyCode}`;
};
